const number = value => typeof value === "number" && Number.isFinite(value) ? value : null;
const xyz = value => ["x", "y", "z"].map(axis => number(value?.[axis]));
const angles = value => Object.fromEntries(["alpha", "beta", "gamma"].map(axis => [axis, number(value?.[axis])]));
const MAX_DURATION = 15 * 60 * 1000;
const MAX_RECORDS = 40000;
const CHUNK_SIZE = 200;

// Combined browser recorder. Values are API-provided, not sensor-calibrated.
// Collection does not require/pretend to know the vehicle alignment.
export function createMotionRecorder({ env, store, key, onState = () => {} }) {
  let started = false, cancelled = false, active = false, capture = null;
  let origin = null, gpsWatch = null, stopPromise = null, stopReason = "user_stopped";
  let buffer = [], queue = Promise.resolve(), chunk = 0, pending = 0, error = null;
  let received = 0, saved = 0, lastMotion = -Infinity, lastOrientation = -Infinity;
  let durationTimer, flushTimer, statusTimer;
  const elapsed = () => origin === null ? 0 : Math.max(0, env.performance.now() - origin);
  const publish = (state, extra = {}) => onState({ state, received_records: received, saved_records: saved, ...extra });

  function flush() {
    if (!buffer.length || error) return;
    const rows = buffer; buffer = [];
    const index = chunk++; pending++;
    queue = queue.then(async () => {
      if (error) return;
      const result = await store.append(key, capture.id, index, rows);
      saved = result.saved_records;
    }).catch(failure => { error = failure.message || "Local save failed."; void stop("storage_error"); })
      .finally(() => { pending--; });
  }
  function add(kind, values) {
    if (!active) return;
    if (elapsed() >= MAX_DURATION) { void stop("time_limit"); return; }
    buffer.push({ sequence: received++, elapsed_ms: elapsed(), kind, ...values });
    if (buffer.length >= CHUNK_SIZE) flush();
    if (pending >= 4) void stop("storage_backpressure");
    else if (received >= MAX_RECORDS) void stop("record_limit");
  }
  function motion(event) {
    if (!active || event.isTrusted !== true || elapsed() - lastMotion < 50) return;
    lastMotion = elapsed();
    add("motion", { event_timestamp_ms: number(event.timeStamp), interval_ms: number(event.interval),
      screen_angle: number(env.screen?.orientation?.angle ?? env.orientation),
      acceleration_mps2: xyz(event.acceleration), acceleration_including_gravity_mps2: xyz(event.accelerationIncludingGravity),
      rotation_rate_dps: angles(event.rotationRate) });
  }
  function orientation(event) {
    if (!active || event.isTrusted !== true || elapsed() - lastOrientation < 50) return;
    lastOrientation = elapsed();
    add("orientation", { event_timestamp_ms: number(event.timeStamp), orientation_degrees: angles(event), absolute: typeof event.absolute === "boolean" ? event.absolute : null });
  }
  const hidden = () => { if (env.document.visibilityState !== "visible") void stop("page_hidden"); };
  const pagehide = () => { void stop("page_left"); };
  function cleanup() {
    env.removeEventListener("devicemotion", motion); env.removeEventListener("deviceorientation", orientation);
    env.removeEventListener("pagehide", pagehide); env.document.removeEventListener("visibilitychange", hidden);
    if (gpsWatch !== null) env.navigator.geolocation.clearWatch(gpsWatch);
    gpsWatch = null;
    env.clearTimeout(durationTimer); env.clearInterval(flushTimer); env.clearInterval(statusTimer);
  }
  function stop(reason = "user_stopped") {
    if (!cancelled) stopReason = reason;
    cancelled = true;
    if (stopPromise) return stopPromise;
    if (!capture) return Promise.resolve(null); // late startup will finish itself
    active = false; cleanup(); publish("saving");
    const duration = elapsed(); flush();
    stopPromise = queue.then(async () => {
      const result = await store.finish(key, capture.id, { reason: error ? "storage_error" : stopReason,
        duration_ms: duration, received_records: received, error });
      publish(error ? "error" : "saved", { record: result, error });
      return result;
    }).catch(failure => { error = failure.message || "Local save failed."; publish("error", { error }); return null; });
    return stopPromise;
  }
  async function start() {
    if (started || cancelled) throw Error("Start a new recording.");
    started = true;
    if (!env.isSecureContext || !env.DeviceMotionEvent) throw Error("This device does not offer motion recording here.");
    if (env.document.visibilityState !== "visible") throw Error("Keep NDG visible to record.");
    publish("permission");
    const permission = api => !api ? Promise.resolve("unavailable")
      : typeof api.requestPermission === "function" ? Promise.resolve(api.requestPermission()).catch(() => "denied") : Promise.resolve("not_required");
    // Both calls occur during the button gesture, before any await/storage work.
    const motionPermission = permission(env.DeviceMotionEvent);
    const orientationPermission = permission(env.DeviceOrientationEvent);
    const [motionAllowed, orientationAllowed] = await Promise.all([motionPermission, orientationPermission]);
    if (cancelled) return false;
    if (!["granted", "not_required"].includes(motionAllowed)) throw Error("Motion permission was not granted. Nothing was recorded.");
    if (env.document.visibilityState !== "visible") throw Error("Keep NDG visible to record.");
    const id = env.crypto.randomUUID();
    origin = env.performance.now();
    capture = await store.begin(key, { id, source: "browser_device_events", frame: "device", acceleration_kind: "gravity_removed",
      started_device_epoch_ms: env.Date.now(), time_origin_ms: number(env.performance.timeOrigin),
      started_monotonic_ms: origin,
      device: env.navigator.userAgent, mount: "landscape_screen_towards_instructor",
      mount_angle: "unmeasured", alignment_status: "unverified", handling_status: "not_detected",
      motion_permission: motionAllowed, orientation_permission: orientationAllowed,
      units: { acceleration: "m/s2", rotation_rate: "degrees/s", orientation: "degrees", gps_speed: "m/s" },
      limits: { max_duration_ms: MAX_DURATION, max_records: MAX_RECORDS, min_interval_ms: 50 },
    });
    if (cancelled || env.document.visibilityState !== "visible") { await stop(cancelled ? stopReason : "page_hidden"); return false; }
    active = true;
    env.addEventListener("devicemotion", motion);
    if (["granted", "not_required"].includes(orientationAllowed)) env.addEventListener("deviceorientation", orientation);
    env.document.addEventListener("visibilitychange", hidden); env.addEventListener("pagehide", pagehide);
    durationTimer = env.setTimeout(() => { void stop("time_limit"); }, MAX_DURATION);
    flushTimer = env.setInterval(flush, 5000);
    statusTimer = env.setInterval(() => publish("recording"), 1000);
    try {
      if (env.navigator.geolocation) gpsWatch = env.navigator.geolocation.watchPosition(position => {
        add("gps", { device_epoch_ms: number(position.timestamp), speed_mps: number(position.coords?.speed),
          heading_degrees: number(position.coords?.heading), horizontal_accuracy_m: number(position.coords?.accuracy),
          speed_accuracy_mps: null }); // Browser horizontal accuracy is NOT speed accuracy.
      }, failure => add("gps_error", { code: number(failure.code) }), { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
      else add("gps_error", { code: null, reason: "unavailable" });
    } catch { add("gps_error", { code: null, reason: "unavailable" }); }
    publish("recording"); return true;
  }
  return { start, stop };
}
